/**
 * Google Analytics 4.
 *
 * Loaded from here rather than as a pair of <script> tags in index.html so the
 * two guards below exist at all. A tag in the HTML fires everywhere the file is
 * served, which means `npm run dev` and `npm run preview` both report as real
 * traffic — and localhost sessions are exactly the ones that skew a low-traffic
 * documentation page.
 *
 * The cost of moving it here is that the tag loads after the bundle parses
 * rather than during head parsing. For a page whose entire purpose is one
 * bundle that has to run anyway, that is a few milliseconds and no page views.
 */

const MEASUREMENT_ID = 'G-1634WGY65T';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

/** Hostnames that are someone running the site locally, not a visitor. */
function isLocalHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname === '' ||
    hostname.endsWith('.local')
  );
}

export function initAnalytics(): void {
  // Vite substitutes a literal at build time: `false` in dev, where this
  // returns immediately, and `true` in the production bundle, where the check
  // is compiled away entirely.
  if (!import.meta.env.PROD) return;
  if (isLocalHost(window.location.hostname)) return;
  // `npm run preview` serves the production build, so PROD alone is not enough.

  if (document.querySelector(`script[src*="${MEASUREMENT_ID}"]`)) return;

  window.dataLayer = window.dataLayer || [];

  // gtag.js reads the Arguments object itself — pushing a plain array of the
  // same values is accepted silently and then never reported.
  function gtag(..._args: unknown[]): void {
    window.dataLayer.push(arguments);
  }

  gtag('js', new Date());
  gtag('config', MEASUREMENT_ID);

  const tag = document.createElement('script');
  tag.async = true;
  tag.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(tag);
}
